import { useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Activity, CalendarClock, CalendarDays, Link as LinkIcon, Settings, Users } from 'lucide-react';
import Icon from '../components/Icon.jsx';
import Loader from '../components/Loader';
import InvitationManager from '../components/InvitationManager';
import ActivityManager from '../components/ActivityManager';
import { useFetchAthleteByUserIdQuery } from '../slices/athleteApiSlice';
import {
  useAutoAssignAthletesMutation,
  useGetAssignmentStatusQuery,
  useSetAthleteExpectedDistanceMutation,
} from '../slices/assignmentsApiSlice';
import {
  useCreateDraftMutation,
  useGetDraftByLeagueQuery,
  useUpdateDraftDetailsMutation,
} from '../slices/draftApiSlice';
import { useFetchLeaguesQuery } from '../slices/leaguesApiSlice';
import { useUpdateLeagueMutation } from '../slices/leaguesApiSlice';
import { useGetLeagueSeasonsQuery } from '../slices/seasonsApiSlice';
import { useGetTeamsByLeagueQuery } from '../slices/teamApiSlice';
import PlayerAssignmentScreen from './PlayerAssignmentScreen.jsx';
import { buildDraftRoomPath } from '../utils/draftRoomMode';
import { setStoredActiveLeagueId } from '../utils/activeLeague';

const formatDate = (dateValue) => {
  if (!dateValue) return null;

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatDateTime = (dateValue) => {
  if (!dateValue) return null;

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const toDateTimeLocalValue = (dateValue) => {
  if (!dateValue) return '';

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return '';

  const offset = date.getTimezoneOffset();
  const localDate = new Date(date.getTime() - offset * 60000);
  return localDate.toISOString().slice(0, 16);
};

const getNextAllowedSeasonStart = (draftDateValue) => {
  const draftDate = new Date(draftDateValue);
  if (Number.isNaN(draftDate.getTime())) return '';

  draftDate.setMinutes(draftDate.getMinutes() + 1);
  return toDateTimeLocalValue(draftDate);
};

const getFutureScheduledDraftState = (draft) => {
  if (!draft?.startTime) {
    return {
      draftStartTime: null,
      hasUpcomingDraftDate: false,
    };
  }

  const draftStartTime = new Date(draft.startTime);
  const hasUpcomingDraftDate = Boolean(
    !Number.isNaN(draftStartTime.getTime())
    && draftStartTime > new Date()
    && draft.status === 'scheduled'
  );

  return {
    draftStartTime,
    hasUpcomingDraftDate,
  };
};

const defaultActivityScoringRules = {
  basePointsPerMile: 1,
  trail: { enabled: false, multiplier: 1.15 },
  ruck: { enabled: false, multiplier: 1.25, minimumWeightLbs: 20 },
  stroller: { enabled: false, multiplier: 1.1 },
  stackingPolicy: 'highest_only',
};

const LeagueManageScreen = () => {
  const navigate = useNavigate();
  const { id: leagueId } = useParams();
  const { userInfo } = useSelector((state) => state.auth);
  const athleteId = userInfo?.athleteId;
  const userId = userInfo?._id;

  useEffect(() => {
    if (leagueId) {
      setStoredActiveLeagueId(leagueId);
    }
  }, [leagueId]);

  const { data: athlete, isLoading: isLoadingAthlete } = useFetchAthleteByUserIdQuery(leagueId ? {
    userId,
    leagueId,
  } : userId, {
    skip: !userId,
  });
  const effectiveAthleteId = athleteId || athlete?._id;

  const { data: userLeague, isLoading: isLoadingLeague, refetch: refetchLeague } = useFetchLeaguesQuery(leagueId, {
    skip: !leagueId,
  });
  const { data: existingDraft, isLoading: isLoadingDraft } = useGetDraftByLeagueQuery(leagueId, {
    skip: !leagueId,
  });
  const { data: leagueSeasons, isLoading: isLoadingLeagueSeasons } = useGetLeagueSeasonsQuery(leagueId, {
    skip: !leagueId,
  });
  const { data: leagueTeams, isLoading: isLoadingLeagueTeams } = useGetTeamsByLeagueQuery(leagueId, {
    skip: !leagueId,
  });
  const isAssignmentAdmin = (userLeague?.admin?._id || userLeague?.admin)?.toString() === effectiveAthleteId?.toString();
  const [showAutoAssignModal, setShowAutoAssignModal] = useState(false);

  const [createDraft, { isLoading: isCreatingDraft }] = useCreateDraftMutation();
  const [updateDraftDetails, { isLoading: isUpdatingDraft }] = useUpdateDraftDetailsMutation();
  const [updateLeague, { isLoading: isUpdatingLeague }] = useUpdateLeagueMutation();
  const [setAthleteExpectedDistance, { isLoading: isSavingExpectedDistance }] = useSetAthleteExpectedDistanceMutation();
  const { data: assignmentStatus, refetch: refetchAssignmentStatus } = useGetAssignmentStatusQuery(leagueId, {
    skip: !leagueId || !isAssignmentAdmin,
  });
  const [autoAssignAthletes, { isLoading: isAutoAssigning }] = useAutoAssignAthletesMutation();

  const [showCreateDraftModal, setShowCreateDraftModal] = useState(false);
  const [shouldOpenInviteModal, setShouldOpenInviteModal] = useState(false);
  const [showMissingSalaryWizard, setShowMissingSalaryWizard] = useState(false);
  const [draftSettings, setDraftSettings] = useState({
    startTime: new Date().toISOString().slice(0, 16),
  });
  const [scoringMode, setScoringMode] = useState('cumulative');
  const [activityScoringRules, setActivityScoringRules] = useState(defaultActivityScoringRules);
  const [editedDraftStart, setEditedDraftStart] = useState('');
  const [adjustedSeasonStart, setAdjustedSeasonStart] = useState('');
  const [missingSalaryAthletes, setMissingSalaryAthletes] = useState([]);
  const [missingSalaryIndex, setMissingSalaryIndex] = useState(0);
  const [distanceByAthleteId, setDistanceByAthleteId] = useState({});

  useEffect(() => {
    if (existingDraft?.startTime) {
      setEditedDraftStart(toDateTimeLocalValue(existingDraft.startTime));
    }
  }, [existingDraft?.startTime]);

  useEffect(() => {
    if (userLeague?.scoringMode) {
      setScoringMode(userLeague.scoringMode);
    }
  }, [userLeague?.scoringMode]);

  useEffect(() => {
    if (userLeague?.activityScoringRules) {
      setActivityScoringRules({
        ...defaultActivityScoringRules,
        ...userLeague.activityScoringRules,
        trail: { ...defaultActivityScoringRules.trail, ...userLeague.activityScoringRules.trail },
        ruck: { ...defaultActivityScoringRules.ruck, ...userLeague.activityScoringRules.ruck },
        stroller: { ...defaultActivityScoringRules.stroller, ...userLeague.activityScoringRules.stroller },
      });
    }
  }, [userLeague?.activityScoringRules]);

  const updateVariationRule = (variation, field, value) => {
    setActivityScoringRules((current) => ({
      ...current,
      [variation]: { ...current[variation], [field]: value },
    }));
  };

  const isLeagueAdmin = isAssignmentAdmin;
  const upcomingSeason = leagueSeasons?.seasons?.find((season) => season.status === 'upcoming');
  const activeSeason = leagueSeasons?.seasons?.find((season) => season.status === 'active');
  const hasResolvedDraftState = !leagueId || (!isLoadingDraft && !isLoadingLeagueSeasons);
  const { hasUpcomingDraftDate } = getFutureScheduledDraftState(existingDraft);
  const shouldShowSetDraftDate = Boolean(
    isLeagueAdmin
    && hasResolvedDraftState
    && !hasUpcomingDraftDate
  );
  const canEditDraftTime = Boolean(
    isLeagueAdmin
    && hasUpcomingDraftDate
  );
  const autoAssignSummary = assignmentStatus?.summary;
  const autoAssignRecommendations = assignmentStatus?.recommendations;
  const hasUnassignedAthletes = Boolean(autoAssignSummary?.unassignedAthletes > 0);
  const canAutoAssign = Boolean(autoAssignRecommendations?.canAutoAssign && hasUnassignedAthletes);
  const draftEditNeedsSeasonAdjustment = Boolean(
    upcomingSeason
    && editedDraftStart
    && new Date(upcomingSeason.startDate) <= new Date(editedDraftStart)
  );
  const minimumAdjustedSeasonStart = editedDraftStart
    ? getNextAllowedSeasonStart(editedDraftStart)
    : '';
  const publicLeagueJoinUrl = !userLeague?.isPrivate && leagueId
    ? `${window.location.origin}/leagues/${leagueId}`
    : '';
  const privateLeagueJoinUrl = userLeague?.isPrivate && leagueId && userLeague?.privateJoinToken
    ? `${window.location.origin}/leagues/${leagueId}?joinToken=${encodeURIComponent(userLeague.privateJoinToken)}`
    : '';

  const currentMissingSalaryAthlete = missingSalaryAthletes[missingSalaryIndex] || null;
  const currentExpectedDistanceValue = currentMissingSalaryAthlete
    ? distanceByAthleteId[currentMissingSalaryAthlete.id] ?? ''
    : '';

  const openMissingSalaryWizard = (salaryValidationError) => {
    const missingAthletes = Array.isArray(salaryValidationError?.athletesWithoutSalary)
      ? salaryValidationError.athletesWithoutSalary
      : [];

    if (!missingAthletes.length) {
      toast.error(salaryValidationError?.message || 'Some athletes are still missing salary data.');
      return;
    }

    setDistanceByAthleteId(
      missingAthletes.reduce((accumulator, athleteEntry) => {
        accumulator[athleteEntry.id] = athleteEntry.expectedWeeklyDistance ?? '';
        return accumulator;
      }, {})
    );
    setMissingSalaryAthletes(missingAthletes);
    setMissingSalaryIndex(0);
    setShowCreateDraftModal(false);
    setShowMissingSalaryWizard(true);
  };

  const submitDraftCreation = async () => {
    const teams = leagueTeams || [];
    const teamIds = teams.filter((team) => team?._id).map((team) => team._id);

    if (teamIds.length === 0) {
      throw new Error('No teams found in this league yet.');
    }

    const randomizedDraftOrder = [...teamIds].sort(() => Math.random() - 0.5);
    return createDraft({
      leagueId,
      startTime: new Date(draftSettings.startTime).toISOString(),
      draftOrder: randomizedDraftOrder,
    }).unwrap();
  };

  const handleCreateDraft = async (e) => {
    e.preventDefault();

    try {
      const newDraft = await submitDraftCreation();

      await refetchLeague();
      toast.success(
        newDraft?.salaryGenerationPerformed
          ? newDraft?.salaryGenerationMode === 'mixed'
            ? 'Draft created. New season salaries were generated from previous season performance plus current ranks.'
            : 'Draft created. Salaries were generated from current ranks.'
          : 'Draft created using the current athlete salaries.'
      );
      setShowCreateDraftModal(false);
      navigate(buildDraftRoomPath(newDraft._id, { chooseView: true }));
    } catch (error) {
      if (error?.data?.code === 'MISSING_ATHLETE_SALARIES' || error?.data?.athletesWithoutSalary?.length) {
        openMissingSalaryWizard(error.data);
        return;
      }

      toast.error(error?.data?.message || error.message || 'Failed to create draft');
    }
  };

  const handleSaveExpectedDistance = async () => {
    if (!currentMissingSalaryAthlete) {
      return;
    }

    const numericExpectedDistance = Number(currentExpectedDistanceValue);
    if (!Number.isFinite(numericExpectedDistance) || numericExpectedDistance < 0) {
      toast.error('Enter a valid non-negative expected distance.');
      return;
    }

    try {
      await setAthleteExpectedDistance({
        athleteId: currentMissingSalaryAthlete.id,
        expectedWeeklyDistance: numericExpectedDistance,
        leagueId,
      }).unwrap();

      const isLastAthlete = missingSalaryIndex >= missingSalaryAthletes.length - 1;
      if (!isLastAthlete) {
        setMissingSalaryIndex((current) => current + 1);
        return;
      }

      await refetchLeague();
      setShowMissingSalaryWizard(false);
      setMissingSalaryAthletes([]);
      setMissingSalaryIndex(0);

      const newDraft = await submitDraftCreation();
      toast.success('Draft created successfully.');
      navigate(buildDraftRoomPath(newDraft._id, { chooseView: true }));
    } catch (error) {
      if (error?.data?.code === 'MISSING_ATHLETE_SALARIES' || error?.data?.athletesWithoutSalary?.length) {
        openMissingSalaryWizard(error.data);
        return;
      }

      toast.error(error?.data?.message || error.message || 'Failed to save expected distance');
    }
  };

  const scrollToAssignments = () => {
    setShowCreateDraftModal(false);
    document.getElementById('league-assignments')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleAutoAssign = async () => {
    try {
      const result = await autoAssignAthletes(leagueId).unwrap();
      toast.success(`Auto-assignment completed! ${result.summary.successfulAssignments} athletes assigned.`);

      if (result.unassignedAthletes.length > 0) {
        toast.warning(`${result.unassignedAthletes.length} athletes could not be assigned due to salary cap constraints.`);
      }

      setShowAutoAssignModal(false);
      refetchAssignmentStatus();
    } catch (error) {
      if (error?.data?.requiresManualSalaryAssignment) {
        toast.error(`${error.data.athletesWithoutActivities.length} athletes need manual salary assignment first`);
      } else {
        toast.error(error?.data?.message || 'Auto-assignment failed');
      }
    }
  };

  const handleCopyPublicLeagueLink = async () => {
    if (!publicLeagueJoinUrl) {
      toast.error('This league is private. Public share links are only available for public leagues.');
      return;
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(publicLeagueJoinUrl);
      } else {
        throw new Error('Clipboard not supported');
      }

      toast.success('Public league link copied to your clipboard.');
    } catch (error) {
      toast.error('Failed to copy the public league link to your clipboard.');
    }
  };

  const handleCopyPrivateLeagueLink = async () => {
    if (!privateLeagueJoinUrl) {
      toast.error('Private share link unavailable right now. Refresh and try again.');
      return;
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(privateLeagueJoinUrl);
      } else {
        throw new Error('Clipboard not supported');
      }

      toast.success('Private league link copied to your clipboard.');
    } catch (error) {
      toast.error('Failed to copy the private league link to your clipboard.');
    }
  };

  if (isLoadingAthlete || isLoadingLeague || isLoadingLeagueTeams) {
    return <Loader />;
  }

  if (!leagueId || !userLeague) {
    return (
      <div className="section-modern">
        <div className="container-modern">
          <Alert variant="warning" className="mt-4 mb-0">
            League context could not be loaded. Return to the league hub and try again.
          </Alert>
        </div>
      </div>
    );
  }

  if (!isLeagueAdmin) {
    return <Navigate to={`/leagues/${leagueId}`} replace />;
  }

  return (
    <div className="section-modern">
      <div className="container-modern">
        <div className="fade-in">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
            <div>
              <h1 className="text-primary mb-2">MANAGE LEAGUE: {userLeague.name}</h1>
              <p className="text-secondary mb-0">Draft setup, invitations, membership repair, and athlete assignment live here.</p>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <Button variant="outline-primary" onClick={() => navigate(`/leagues/${leagueId}`)}>
                Back to League Hub
              </Button>
              <Button variant="primary" onClick={() => setShouldOpenInviteModal(true)}>
                Send Invitation
              </Button>
              {existingDraft?._id ? (
                <Button variant="primary" onClick={() => navigate(buildDraftRoomPath(existingDraft._id, { chooseView: true }))}>
                  Open Draft Room
                </Button>
              ) : null}
            </div>
          </div>

          {shouldShowSetDraftDate && (
            <Alert variant="danger" className="mb-4">
              <Alert.Heading className="fs-6 mb-1">Set the next draft date</Alert.Heading>
              <p className="mb-2">
                This league needs a new future draft date before the next season can be created.
              </p>
              <p className="mb-0">
                Starting the next season clears the live team rosters, so schedule the new draft first.
              </p>
            </Alert>
          )}

          <div className="grid-modern grid-cols-2 mb-4">
            <Card className="modern-card">
              <div className="modern-card-header compact-header">
                <h2 className="modern-card-title"><Icon icon={Settings} size={18} className="me-2 align-middle" /> Draft Controls</h2>
                <p className="modern-card-subtitle">Schedule the first draft or update the next scheduled one.</p>
              </div>
              <Card.Body className="d-flex flex-column gap-3">
                <div>
                  <div className="text-secondary small mb-1">Current status</div>
                  <div className="fw-semibold">{hasUpcomingDraftDate ? existingDraft.status : existingDraft ? 'Previous draft completed' : 'No draft scheduled'}</div>
                </div>
                {hasUpcomingDraftDate ? (
                  <div>
                    <div className="text-secondary small mb-1">Scheduled start</div>
                    <div className="fw-semibold">{formatDateTime(existingDraft.startTime)}</div>
                  </div>
                ) : null}
                <div className="d-flex gap-2 flex-wrap">
                  {shouldShowSetDraftDate && (
                    <Button variant="primary" onClick={() => setShowCreateDraftModal(true)}>
                      Set Draft Date
                    </Button>
                  )}
                  {canEditDraftTime && (
                    <Button variant="primary" onClick={() => setShowCreateDraftModal(true)}>
                      Edit Draft Date
                    </Button>
                  )}
                  {!shouldShowSetDraftDate && !canEditDraftTime && !existingDraft && (
                    <span className="text-secondary">Draft setup will become available once the league is ready for scheduling.</span>
                  )}
                </div>
                <div className="pt-2 border-top">
                  <div className="text-secondary small mb-1">Draft format options</div>
                  <div className="small text-secondary mb-3">
                    Auto-assign is a simple alternative to the live draft and quickly places athletes onto teams for you.
                    The live draft lets owners interactively choose players together in a timed draft lobby.
                  </div>
                  <div className="small text-secondary mb-3">
                    {hasUnassignedAthletes
                      ? autoAssignRecommendations?.message
                      : 'All athletes are already assigned to league teams.'}
                  </div>
                  <div className="d-flex gap-2 flex-wrap">
                    <Button
                      variant="outline-primary"
                      onClick={() => setShowAutoAssignModal(true)}
                      disabled={!hasUnassignedAthletes || isAutoAssigning}
                    >
                      {isAutoAssigning ? 'Assigning...' : 'Auto-Assign Athletes'}
                    </Button>
                    <Button variant="outline-secondary" onClick={scrollToAssignments}>
                      Review Assignments
                    </Button>
                  </div>
                </div>
              </Card.Body>
            </Card>

            <Card className="modern-card">
              <div className="modern-card-header compact-header">
                <h2 className="modern-card-title"><Icon icon={Activity} size={18} className="me-2 align-middle" /> Activity Scoring</h2>
                <p className="modern-card-subtitle">Set mileage bonuses for future seasons. Each season keeps the rules it starts with.</p>
              </div>
              <Card.Body className="d-flex flex-column gap-3">
                <Form.Group>
                  <Form.Label className="text-secondary small">Base points per mile</Form.Label>
                  <Form.Control type="number" min="0" max="100" step="0.1" value={activityScoringRules.basePointsPerMile} onChange={(event) => setActivityScoringRules((current) => ({ ...current, basePointsPerMile: event.target.value }))} />
                </Form.Group>
                {[
                  ['trail', 'Trail run'],
                  ['ruck', 'Ruck'],
                  ['stroller', 'Stroller'],
                ].map(([variation, label]) => (
                  <div key={variation} className="d-flex flex-wrap align-items-end gap-3 border-top pt-3">
                    <Form.Check type="switch" id={`${variation}-scoring-enabled`} label={label} checked={activityScoringRules[variation].enabled} onChange={(event) => updateVariationRule(variation, 'enabled', event.target.checked)} />
                    <Form.Group>
                      <Form.Label className="text-secondary small">Multiplier</Form.Label>
                      <Form.Control type="number" min="1" max="5" step="0.05" disabled={!activityScoringRules[variation].enabled} value={activityScoringRules[variation].multiplier} onChange={(event) => updateVariationRule(variation, 'multiplier', event.target.value)} />
                    </Form.Group>
                    {variation === 'ruck' && (
                      <Form.Group>
                        <Form.Label className="text-secondary small">Minimum pack weight (lb)</Form.Label>
                        <Form.Control type="number" min="0" max="200" step="1" disabled={!activityScoringRules.ruck.enabled} value={activityScoringRules.ruck.minimumWeightLbs} onChange={(event) => updateVariationRule('ruck', 'minimumWeightLbs', event.target.value)} />
                      </Form.Group>
                    )}
                  </div>
                ))}
                <div className="text-secondary small">When more than one variation applies, only the highest multiplier counts. Trail runs reported by Strava or Garmin are detected automatically; athlete declarations require manager approval.</div>
                <div>
                  <Button
                    variant="primary"
                    disabled={isUpdatingLeague}
                    onClick={async () => {
                      try {
                        await updateLeague({
                          id: leagueId,
                          name: userLeague.name,
                          description: userLeague.description,
                          isPrivate: userLeague.isPrivate,
                          activityScoringRules,
                        }).unwrap();
                        toast.success('Activity scoring rules saved for future seasons.');
                      } catch (error) {
                        toast.error(error?.data?.message || 'Failed to update activity scoring rules');
                      }
                    }}
                  >
                    {isUpdatingLeague ? 'Saving...' : 'Save Scoring Rules'}
                  </Button>
                </div>
              </Card.Body>
            </Card>

            <Card className="modern-card">
              <div className="modern-card-header compact-header">
                <h2 className="modern-card-title"><Icon icon={CalendarDays} size={18} className="me-2 align-middle" /> Season Context</h2>
                <p className="modern-card-subtitle">Keep draft timing aligned with the next season window.</p>
              </div>
              <Card.Body className="d-flex flex-column gap-3">
                {activeSeason ? (
                  <div>
                    <div className="text-secondary small mb-1">Active season</div>
                    <div className="fw-semibold">{formatDate(activeSeason.startDate)} to {formatDate(activeSeason.endDate)}</div>
                  </div>
                ) : (
                  <div className="text-secondary">No active season is running right now.</div>
                )}
                {upcomingSeason ? (
                  <div>
                    <div className="text-secondary small mb-1">Upcoming season</div>
                    <div className="fw-semibold">Season {upcomingSeason.seasonNumber} starts {formatDateTime(upcomingSeason.startDate)}</div>
                  </div>
                ) : (
                  <div className="text-secondary">No upcoming season start date is scheduled yet.</div>
                )}
                <Button variant="outline-primary" onClick={() => navigate(`/admin?leagueId=${leagueId}`)}>
                  <Icon icon={CalendarClock} size={16} className="me-1 align-middle" /> Open Season Setup
                </Button>
              </Card.Body>
            </Card>

            <Card className="modern-card">
              <div className="modern-card-header compact-header">
                <h2 className="modern-card-title"><Icon icon={LinkIcon} size={18} className="me-2 align-middle" /> Shareable League Link</h2>
                <p className="modern-card-subtitle">Share this league with runners who want to join it.</p>
              </div>
              <Card.Body className="d-flex flex-column gap-3">
                {userLeague?.isPrivate ? (
                  <>
                    <div className="text-secondary small">
                      Anyone with this private link can open the league page, sign in if needed, and join the league. Treat it like an invite link.
                    </div>
                    <Form.Group>
                      <Form.Label className="text-secondary small">Private share link</Form.Label>
                      <Form.Control type="text" value={privateLeagueJoinUrl} readOnly onFocus={(event) => event.target.select()} />
                    </Form.Group>
                    <div>
                      <Button variant="primary" onClick={handleCopyPrivateLeagueLink} disabled={!privateLeagueJoinUrl}>
                        Copy Private Link
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-secondary small">
                      Anyone with this link can open the league page, sign in if needed, and join the public league from there.
                    </div>
                    <Form.Group>
                      <Form.Label className="text-secondary small">Shareable join link</Form.Label>
                      <Form.Control type="text" value={publicLeagueJoinUrl} readOnly onFocus={(event) => event.target.select()} />
                    </Form.Group>
                    <div>
                      <Button variant="primary" onClick={handleCopyPublicLeagueLink}>
                        Copy Public Link
                      </Button>
                    </div>
                  </>
                )}
              </Card.Body>
            </Card>

            <Card className="modern-card">
              <div className="modern-card-header compact-header">
                <h2 className="modern-card-title"><Icon icon={Settings} size={18} className="me-2 align-middle" /> Competition Mode</h2>
                <p className="modern-card-subtitle">Choose between season-long cumulative standings or weekly head-to-head matchups.</p>
              </div>
              <Card.Body className="d-flex flex-column gap-3">
                <Form.Group>
                  <Form.Label className="text-secondary small">Scoring mode</Form.Label>
                  <Form.Select value={scoringMode} onChange={(e) => setScoringMode(e.target.value)}>
                    <option value="cumulative">Cumulative season points</option>
                    <option value="head_to_head">Weekly head-to-head matchups</option>
                  </Form.Select>
                </Form.Group>
                <div className="text-secondary small">
                  {scoringMode === 'head_to_head'
                    ? 'Teams play a scheduled weekly matchup and standings are based on wins and losses. Weekly scores still come from the same athlete events and owner bonus rules.'
                    : 'Teams are ranked by total season points across the full schedule.'}
                </div>
                <div>
                  <Button
                    variant="primary"
                    disabled={isUpdatingLeague || scoringMode === (userLeague?.scoringMode || 'cumulative')}
                    onClick={async () => {
                      try {
                        await updateLeague({
                          id: leagueId,
                          name: userLeague.name,
                          description: userLeague.description,
                          isPrivate: userLeague.isPrivate,
                          scoringMode,
                        }).unwrap();
                        toast.success('League competition mode updated.');
                      } catch (error) {
                        toast.error(error?.data?.message || 'Failed to update league mode');
                      }
                    }}
                  >
                    {isUpdatingLeague ? 'Saving...' : 'Save Mode'}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </div>

          <div id="league-assignments" className="mb-4">
            <PlayerAssignmentScreen
              leagueIdOverride={leagueId}
              embedded
              membershipManager={(
                <div className="modern-card p-3">
                  <InvitationManager
                    leagueId={leagueId}
                    leagueName={userLeague.name}
                    isOwner
                    memberCount={Array.isArray(userLeague.athletes) ? userLeague.athletes.length : undefined}
                    athletes={Array.isArray(userLeague.athletes) ? userLeague.athletes : []}
                    leagueTeams={Array.isArray(userLeague.teams) ? userLeague.teams : []}
                    hasActiveSeason={Boolean(activeSeason)}
                    shouldOpenInviteModal={shouldOpenInviteModal}
                    onInviteModalOpened={() => setShouldOpenInviteModal(false)}
                  />
                </div>
              )}
              activityManager={(
                <ActivityManager leagueId={leagueId} athletes={userLeague.athletes || []} />
              )}
            />
          </div>
        </div>
      </div>

      <Modal show={showCreateDraftModal} onHide={() => setShowCreateDraftModal(false)} centered>
        <Modal.Header closeButton style={{ backgroundColor: 'var(--bg-primary)', borderBottom: '1px solid var(--border-light)' }}>
          <Modal.Title className="text-primary">{canEditDraftTime ? 'Update Draft Date' : 'Create League Draft'}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: 'var(--bg-primary)' }}>
          {!canEditDraftTime && (
            <>
              <div className="mb-4 p-3" style={{ backgroundColor: 'var(--bg-secondary)', borderRadius: '8px', border: '1px solid var(--border-light)' }}>
                <h6 className="text-primary mb-2">Draft Settings</h6>
                <div className="text-muted small">
                  <div>Snake draft</div>
                  <div>60 seconds per pick</div>
                  <div>Rounds calculated automatically from available athletes</div>
                </div>
              </div>

              <Alert variant="warning" className="mb-4">
                <Alert.Heading className="fs-6">Salary setup for the draft</Alert.Heading>
                <p className="mb-2">
                  When you confirm the draft date, LeagueRunner will use the current athlete salaries for the draft. If salaries have not been generated yet, LeagueRunner will generate them before creating the draft.
                </p>
                <p className="mb-2">
                  Returning athletes use previous-season performance. New athletes use the current rankings from the assignment section.
                </p>
                <p className="mb-0">
                  New athletes are auto-ranked when they join, but you may want to review those rankings before moving forward.
                </p>
              </Alert>

              <Form onSubmit={handleCreateDraft}>
                <div className="form-group-modern">
                  <label className="form-label-modern">Start Time</label>
                  <input
                    type="datetime-local"
                    name="startTime"
                    className="form-input-modern"
                    value={draftSettings.startTime}
                    onChange={(e) => setDraftSettings((prev) => ({ ...prev, startTime: e.target.value }))}
                  />
                </div>

                <div className="d-flex flex-column gap-2 mt-4">
                  <button
                    type="button"
                    className="btn btn-outline-modern w-100"
                    onClick={scrollToAssignments}
                  >
                    Cancel and Review Athlete Rankings
                  </button>
                  <button type="submit" className="btn btn-primary-modern w-100" disabled={isCreatingDraft}>
                    {isCreatingDraft ? 'Creating...' : 'All Set. Create Draft!'}
                  </button>
                </div>
              </Form>
            </>
          )}

          {canEditDraftTime && (
            <form
              className="mt-3"
              onSubmit={async (e) => {
                e.preventDefault();
                try {
                  if (draftEditNeedsSeasonAdjustment) {
                    if (!adjustedSeasonStart) {
                      toast.error('Choose a new season start date after the updated draft date.');
                      return;
                    }

                    if (new Date(adjustedSeasonStart) <= new Date(editedDraftStart)) {
                      toast.error('Season start date must be after the updated draft date.');
                      return;
                    }
                  }

                  const result = await updateDraftDetails({
                    id: existingDraft._id,
                    startTime: new Date(editedDraftStart).toISOString(),
                    seasonStartDate: draftEditNeedsSeasonAdjustment
                      ? new Date(adjustedSeasonStart).toISOString()
                      : undefined,
                  }).unwrap();

                  if (result.updatedSeason) {
                    toast.success('Draft time updated and upcoming season start adjusted.');
                  } else {
                    toast.success('Draft start time updated.');
                  }

                  if (result.updatedSeason?.startDate) {
                    setAdjustedSeasonStart(toDateTimeLocalValue(result.updatedSeason.startDate));
                  }

                  setShowCreateDraftModal(false);
                } catch (error) {
                  toast.error(error?.data?.message || 'Failed to update draft time');
                }
              }}
            >
              <label className="form-label-modern">Update Existing Draft</label>
              <input
                type="datetime-local"
                name="draftStart"
                className="form-input-modern mb-2"
                value={editedDraftStart}
                min={new Date().toISOString().slice(0, 16)}
                onChange={(e) => {
                  const nextDraftStart = e.target.value;
                  setEditedDraftStart(nextDraftStart);

                  if (upcomingSeason && new Date(upcomingSeason.startDate) <= new Date(nextDraftStart)) {
                    const suggestedSeasonStart = getNextAllowedSeasonStart(nextDraftStart);
                    setAdjustedSeasonStart((currentValue) => {
                      if (!currentValue || new Date(currentValue) <= new Date(nextDraftStart)) {
                        return suggestedSeasonStart;
                      }
                      return currentValue;
                    });
                  }
                }}
              />
              {upcomingSeason && (
                <p className="text-muted small mb-2">
                  Upcoming season {upcomingSeason.seasonNumber} currently starts on {new Date(upcomingSeason.startDate).toLocaleString()}.
                </p>
              )}
              {draftEditNeedsSeasonAdjustment && (
                <>
                  <Alert variant="warning" className="mb-2">
                    This draft update would move the draft after the upcoming season start. Set a new season start date as well.
                  </Alert>
                  <input
                    type="datetime-local"
                    name="seasonStart"
                    className="form-input-modern mb-2"
                    value={adjustedSeasonStart}
                    min={minimumAdjustedSeasonStart}
                    onChange={(e) => setAdjustedSeasonStart(e.target.value)}
                    required
                  />
                </>
              )}
              <button type="submit" className="btn btn-outline-modern w-100" disabled={isUpdatingDraft}>
                {isUpdatingDraft ? 'Saving...' : 'Update Draft Time'}
              </button>
            </form>
          )}
        </Modal.Body>
      </Modal>

      <Modal show={showAutoAssignModal} onHide={() => setShowAutoAssignModal(false)} centered>
        <Modal.Header closeButton style={{ backgroundColor: 'var(--bg-primary)', borderBottom: '1px solid var(--border-light)' }}>
          <Modal.Title className="text-primary">Confirm Auto-Assignment</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: 'var(--bg-primary)' }}>
          <p>
            This will automatically assign all unassigned athletes to teams based on:
          </p>
          <ul>
            <li>Any rank-generated, manual, or calculated salaries already saved</li>
            <li>Athlete performance-based salary calculations</li>
            <li>Team salary cap constraints</li>
            <li>Fair distribution algorithm</li>
          </ul>
          <p>
            <strong>{autoAssignSummary?.athletesReadyForAutoAssign || 0}</strong> athletes are ready for auto-assignment.
          </p>
          <Alert variant="info" className="mb-0">
            This action cannot be easily undone. Assignments will need to be manually adjusted if needed.
          </Alert>
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: 'var(--bg-primary)', borderTop: '1px solid var(--border-light)' }}>
          <Button variant="secondary" onClick={() => setShowAutoAssignModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleAutoAssign} disabled={!canAutoAssign || isAutoAssigning}>
            {isAutoAssigning ? 'Auto-Assigning...' : 'Proceed with Auto-Assignment'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showMissingSalaryWizard}
        onHide={() => {
          setShowMissingSalaryWizard(false);
          setShowCreateDraftModal(true);
        }}
        centered
      >
        <Modal.Header closeButton style={{ backgroundColor: 'var(--bg-primary)', borderBottom: '1px solid var(--border-light)' }}>
          <Modal.Title className="text-primary">Set Missing Athlete Salaries</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: 'var(--bg-primary)' }}>
          <Alert variant="warning" className="mb-4">
            <Alert.Heading className="fs-6">Some athletes do not have a salary set</Alert.Heading>
            <p className="mb-0">
              Salaries are based on expected distance run per week on average. Set that distance for each athlete below to continue creating the draft.
            </p>
          </Alert>

          {currentMissingSalaryAthlete ? (
            <>
              <div className="mb-3 text-muted small">
                Athlete {missingSalaryIndex + 1} of {missingSalaryAthletes.length}
              </div>

              <div className="mb-4 p-3" style={{ backgroundColor: 'var(--bg-secondary)', borderRadius: '8px', border: '1px solid var(--border-light)' }}>
                <div className="mb-2"><strong>Name:</strong> {currentMissingSalaryAthlete.name || 'Unknown athlete'}</div>
                <div className="mb-2"><strong>Email:</strong> {currentMissingSalaryAthlete.email || 'No email on file'}</div>
                <div><strong>Team:</strong> {currentMissingSalaryAthlete.teamName || 'No team'}</div>
              </div>

              <Form.Group>
                <Form.Label>Expected distance per week</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="0.1"
                  value={currentExpectedDistanceValue}
                  onChange={(e) => {
                    const nextValue = e.target.value;
                    setDistanceByAthleteId((current) => ({
                      ...current,
                      [currentMissingSalaryAthlete.id]: nextValue,
                    }));
                  }}
                  placeholder="Enter expected weekly distance"
                />
              </Form.Group>

              <div className="d-flex gap-2 mt-4">
                <Button
                  variant="outline-secondary"
                  onClick={() => setMissingSalaryIndex((current) => Math.max(0, current - 1))}
                  disabled={missingSalaryIndex === 0 || isSavingExpectedDistance}
                >
                  Previous
                </Button>
                <Button
                  variant="primary"
                  className="ms-auto"
                  onClick={handleSaveExpectedDistance}
                  disabled={isSavingExpectedDistance}
                >
                  {isSavingExpectedDistance
                    ? 'Saving...'
                    : missingSalaryIndex >= missingSalaryAthletes.length - 1
                      ? 'Save and Create Draft'
                      : 'Save and Next'}
                </Button>
              </div>
            </>
          ) : (
            <Alert variant="info" className="mb-0">
              No athletes are currently missing salary data.
            </Alert>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default LeagueManageScreen;